<script setup lang="ts">
import { computed, inject, ref, watchEffect } from 'vue'
import { Field, type PrivateFormContext, type useForm } from 'vee-validate'
import CrdColorPicker from '@/components/common/crd-color-picker/CrdColorPicker.vue'
import type { ColorPickerColor, IGradientSelector, ProjectSettings } from '@/components/projects/interfaces.ts'
import type { FormSettings } from '@/components/interfaces/project-settings-view.interfaces.ts'
import CrdRange from '../../../common/CrdRange.vue'
import { useTranslations } from '@/composables/useTranslations.ts'
import { ProjectSettingsField, ProvideElement } from '@/enums.ts'
import { ProjectSettingFormKey } from '@/components/projects/settings.types.ts'

const { t } = useTranslations()
const { widgetVisual } = inject(ProvideElement.ProjectSettingsFormSettings) as FormSettings
const { defineField, setFieldValue } = inject(ProjectSettingFormKey)
// @ts-expect-error Jevgeni fix it
const [ enabled ] = defineField<string>(ProjectSettingsField.WidgetVisualEnabled)
const [ colors ] = defineField(ProjectSettingsField.WidgetVisualGradientColors)

const pickerRefs = ref<Array<HTMLDivElement | null>>([null, null])
const isColorPickerVisible = ref<[boolean, boolean]>([false, false])
const initialColorValues = ref<string[]>([colors.value[0], colors.value[1]])


const isClickInside = (element: HTMLElement, target: EventTarget | null): boolean => {
  return !!target && element.contains(target as Node)
}

watchEffect((onCleanup) => { // TODO: Revalidate solution
  const handlers = pickerRefs.value.map((el, index) => {
    if (!el) return () => {}

    const handler = (event: MouseEvent) => {
      if (!isClickInside(el, event.target)) {
        isColorPickerVisible.value[index] = false
      }
    }

    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  })

  onCleanup(() => {
    handlers.forEach((cleanup) => cleanup?.())
  })
})
</script>

<template>
  <div class="p-2">
    <div class="flex items-center gap-4">
      <p class="text-sm md:text-base font-semibold">{{ t('settings.primary.enableGradient') }}</p>
      <label class="flex items-center gap-2 cursor-pointer">
        <Field
          :value="true"
          :unchecked-value="false"
          name="widgetVisual.gradients.enabled"
          type="checkbox"
          v-model="enabled"
        />
      </label>
    </div>

    <div :class="{ 'opacity-50 pointer-events-none': !enabled }">
      <div class="mb-4">
        <CrdRange
          :label="t('settings.primary.gradientAngle')"
          :description="t('settings.primary.hereYouCanChooseAngleOfTheGradient')"
          :name="ProjectSettingsField.WidgetVisualDegree"
          :min="widgetVisual.gradients.degree.min"
          :max="widgetVisual.gradients.degree.max"
          :step="widgetVisual.gradients.degree.step"
          unit="°"
          class="w-full mt-2.5"
          :disabled="!enabled"
        />
      </div>
      <div class="flex gap-4 relative">
        <div v-for="(color, index) in colors" :key="index" class="relative">
          <Field
            :name="`${ProjectSettingsField.WidgetVisualGradientColors}[${index}]`"
            v-model="colors[index]"
          >
            <span class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('settings.primary.color') }} {{ index + 1 }}
            </span>
            <div
              class="size-8 md:size-12 rounded-full cursor-pointer border-2 border-gray-200"
              :style="{ backgroundColor: color }"
              @click="() => {
                isColorPickerVisible[index] = !isColorPickerVisible[index]
                colors[index] = initialColorValues[index]
              }"
            />
            <div
              v-if="isColorPickerVisible[index]"
              :ref="(el) => (pickerRefs[index] = el as HTMLDivElement)"
              class="absolute top-4 left-3 z-10"
              @click.stop
            >
              <CrdColorPicker
                v-if="isColorPickerVisible[index]"
                @change="(color: ColorPickerColor) => {
                  setFieldValue(`${ProjectSettingsField.WidgetVisualGradientColors}[${index}]`, color.hex)
                }"
                @saved="(color: ColorPickerColor) => {
                  setFieldValue(`${ProjectSettingsField.WidgetVisualGradientColors}[${index}]`, color.hex)
                  initialColorValues[index] = color.hex
                  isColorPickerVisible[index] = false
                }"
                @cancel="() => {
                  isColorPickerVisible[index] = false
                  colors[index] = initialColorValues[index]
                }"
                class="absolute z-10 top-12 left-0"
              />
            </div>
          </Field>
        </div>
      </div>
    </div>
    <!--<ErrorMessage
      :name="name"
      class="text-sm font-500 text-red-400 absolute top-full text-center w-full"
    />-->
  </div>
</template>
